/*
Copyright (c) 2012-2026 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/
/** @jsx jsx */
import { jsx, css } from "@emotion/core"
import { FC, useMemo } from "react"
import { useSelector } from "react-redux"
import { AppState } from "store/store.type"
import { isItemWithoutPhotoMode, isMenuFilterActive } from "utils/menu_helper"
// import from root will cause `node-sass` not found.
import { MarketingBannerV2 } from "troy-common/marketing-banner"
import { TroyWebTracker } from "utils/tracking"
import { marketingBannerStyle } from "pages/menu/style"
import { parseJSONSafely } from "utils/misc"
import { useEnablePartialMenu } from "hooks/useEnablePartialMenu"
import { EmptySectionCategoryWrapper } from "../empty"
import { useMenuOverviewItems } from "./hooks/useMenuOverviewItems"
import { useMenuAutoSelection } from "./hooks/useMenuAutoSelection"
import { useSelectionSync } from "./hooks/useSelectionSync"
import {
  MenuOverviewCategories,
  MenuOverviewItems,
  MenuOverviewDepartments,
  MenuOverviewSubDepartments,
  MenuOverviewDepartmentsV3,
  MenuOverviewSubDepartmentsV3,
  MenuOverviewItemsV3,
} from "./lists"
import {
  cardStyle,
  nonRoundRightCardStyle,
  nonRoundLeftCardStyle,
} from "./styles"

interface IProps {
  hasAction?: boolean
  routePrefix?: string
}

export const MenuOverviewTable: FC<IProps> = ({
  hasAction = true,
  routePrefix,
}: IProps) => {
  const {
    overview: { categories, sellingTime },
    isMartDepartmentStructure,
    itemFilter,
  } = useSelector((state: AppState) => state.menu)

  const enablePartialMenu = useEnablePartialMenu()

  const isFilterActive = isMenuFilterActive(itemFilter, sellingTime)

  const shouldUseV3Components = enablePartialMenu && !isFilterActive

  // Auto-selection hook - handles department/subdepartment selection when filters change
  // Only runs when enablePartialMenu=true (legacy mode uses Redux handlers)
  useMenuAutoSelection()

  // Syncs multi-select state between bulkSelectionsV3 (V3, no filter) and
  // itemSelections (legacy, filter active) when the user toggles filters
  // while the drawer is open
  useSelectionSync()

  // Get filtered items for photo coverage calculation (mart or Redux per hook)
  const {
    filteredItems,
    isLoading: isOverviewItemsLoading,
  } = useMenuOverviewItems()

  const itemWithoutPhotoCount: number = useMemo(() => {
    if (isItemWithoutPhotoMode(itemFilter)) {
      return filteredItems.length
    }
    return 0
  }, [filteredItems, itemFilter])

  const showEmptyStateForItemPhotoCoverage = () => {
    return (
      isItemWithoutPhotoMode(itemFilter) &&
      itemWithoutPhotoCount === 0 &&
      !isOverviewItemsLoading
    )
  }

  return showEmptyStateForItemPhotoCoverage() ? (
    <div
      css={css`
        display: flex;
        justify-content: center;
        height: 40vh;
        flex-direction: column;
        align-items: center;
      `}
    >
      <img
        src={require("../../../../assets/images/menu/photo_filter_empty_state.svg")}
      />

      <div
        css={css`
          font-size: 16px;
          font-weight: 400;
          line-height: 24px;
          margin-top: 8px;
          color: #3d3d3d;
        `}
      >
        You have perfect item photo coverage!
      </div>
      <div
        css={css`
          font-size: 12px;
          font-weight: 400;
          line-height: 16px;
          color: #a3a3a3;
        `}
      >
        All items in your search or filter have photos added
      </div>
    </div>
  ) : (
    <EmptySectionCategoryWrapper categories={categories}>
      <div
        css={css`
          display: flex;
          flex-grow: 1;
          flex-flow: row;
        `}
      >
        <div
          css={css`
            width: 330px;
          `}
        >
          {isMartDepartmentStructure && shouldUseV3Components && (
            <MenuOverviewDepartmentsV3 />
          )}
          {isMartDepartmentStructure && !shouldUseV3Components && (
            <MenuOverviewDepartments
              css={css`
                ${cardStyle}
                ${nonRoundRightCardStyle}
              `}
              hasAction={hasAction}
            />
          )}
          {!isMartDepartmentStructure && (
            <MenuOverviewCategories
              css={css`
                ${cardStyle}
                ${nonRoundRightCardStyle}
              `}
              hasAction={hasAction}
            />
          )}
        </div>
        {isMartDepartmentStructure && (
          <div
            css={css`
              width: 330px;
            `}
          >
            {shouldUseV3Components ? (
              <MenuOverviewSubDepartmentsV3 />
            ) : (
              <MenuOverviewSubDepartments
                css={css`
                  ${cardStyle}
                  ${nonRoundRightCardStyle}
                  ${nonRoundLeftCardStyle}
                  border-left: none;
                `}
                hasAction={hasAction}
              />
            )}
          </div>
        )}
        <div
          css={css`
            flex-grow: 1;
            min-width: 350px;
          `}
        >
          {isMartDepartmentStructure && shouldUseV3Components ? (
            <MenuOverviewItemsV3 routePrefix={routePrefix} />
          ) : (
            <MenuOverviewItems
              css={css`
                ${cardStyle}
                ${nonRoundLeftCardStyle}
                border-left: none;
              `}
              hasAction={hasAction}
              routePrefix={routePrefix}
            />
          )}
        </div>
      </div>

      <MarketingBannerV2
        css={marketingBannerStyle}
        variant="upsellMFCMenu"
        tab="Menu"
        subtab="MFC"
        trackEvent={(
          category: string,
          action: string,
          labelsString: string,
        ) => {
          TroyWebTracker.sendEvent({
            // @ts-ignore
            category,
            // @ts-ignore
            action,
            labels: {
              ...parseJSONSafely(labelsString ?? "{}"),
            },
          })
        }}
      />
    </EmptySectionCategoryWrapper>
  )
}
